import { Collection, CollectionFormData, CollectionFilter } from '@/features/collections/types';
import { toast } from 'sonner';

// API base URL - should be configured based on environment
const API_BASE_URL = `${import.meta.env.VITE_API_BASE_URL || 'https://obta-api.trade.workers.dev'}/api`;

// API token for production
const API_TOKEN = 'obta-production-api-token-2025';

// Enhanced pagination interface
export interface PaginatedCollections {
  collections: Collection[];
  total: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
}

// Helper function to handle API errors
const handleApiError = (error: any): never => {
  console.error('Collections API Error:', error);
  toast.error(error.message || 'An error occurred while communicating with the API');
  throw new Error(error.message || 'An error occurred while communicating with the API');
};

// Helper function to convert various timestamp formats to Unix timestamp
const normalizeTimestamp = (timestamp: any): number => {
  if (!timestamp) {
    return Math.floor(Date.now() / 1000);
  }
  
  // If it's already a number (Unix timestamp), return it
  if (typeof timestamp === 'number') {
    // Check if it's in milliseconds (13 digits) or seconds (10 digits)
    return timestamp > 9999999999 ? Math.floor(timestamp / 1000) : timestamp;
  }
  
  // If it's a string, try to parse it as a date
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) {
      // If parsing failed, return current timestamp
      return Math.floor(Date.now() / 1000);
    }
    return Math.floor(date.getTime() / 1000);
  }
  
  // Fallback to current timestamp
  return Math.floor(Date.now() / 1000);
};

export const CollectionsService = {
  /**
   * Get collections with optional filtering and pagination
   */
  async getCollections(
    page: number = 1,
    pageSize: number = 20,
    filters?: CollectionFilter
  ): Promise<PaginatedCollections> {
    try {
      console.log('🔍 Fetching collections from API...');
      
      const params = new URLSearchParams();
      params.append('limit', pageSize.toString());
      params.append('offset', ((page - 1) * pageSize).toString());
      
      if (filters) {
        if (filters.status) {
          filters.status.forEach(status => params.append('status', status));
        }
        if (filters.visibility) {
          filters.visibility.forEach(visibility => params.append('visibility', visibility));
        }
        if (filters.search_term) {
          params.append('search_term', filters.search_term);
        }
      }

      const response = await fetch(`${API_BASE_URL}/collections?${params.toString()}`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch collections: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      console.log('📊 Collections API Response:', { 
        total: data.total, 
        resultCount: data.results?.length || data.length
      });
      
      const collections = (data.results || data).map((collection: any) => this.transformCollection(collection));
      
      // Calculate proper pagination info
      const total = data.total || collections.length;
      const totalPages = Math.ceil(total / pageSize);
      
      return {
        collections,
        total,
        currentPage: page,
        totalPages,
        pageSize,
      };
    } catch (error) {
      console.error('Error in getCollections:', error);
      throw error;
    }
  },

  /**
   * Get a single collection by ID with documents
   */
  async getCollection(id: string): Promise<Collection> {
    try {
      console.log(`🔍 Fetching collection ${id} from API...`);
      
      const response = await fetch(`${API_BASE_URL}/collections/${id}`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch collection: ${response.status} ${response.statusText}`);
      }

      const collection = await response.json();
      return this.transformCollection(collection);
    } catch (error) {
      console.error(`Error fetching collection ${id}:`, error);
      throw error;
    }
  },

  /**
   * Create a new collection
   */
  async createCollection(collectionData: CollectionFormData): Promise<Collection> {
    try {
      console.log('📝 Creating new collection...', collectionData);
      
      const response = await fetch(`${API_BASE_URL}/collections`, {
        method: 'POST',
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(collectionData),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error("Create collection error response:", errorBody);
        throw new Error(`Failed to create collection: ${response.status} ${response.statusText} - ${errorBody}`);
      }

      const collection = await response.json();
      const transformedCollection = this.transformCollection(collection);
      
      toast.success('Collection created successfully!');
      return transformedCollection;
    } catch (error) {
      console.error('Error creating collection:', error);
      return handleApiError(error);
    }
  },

  /**
   * Update an existing collection
   */
  async updateCollection(id: string, collectionData: Partial<CollectionFormData>): Promise<Collection> {
    try {
      console.log(`📝 Updating collection ${id}...`, collectionData);
      
      const response = await fetch(`${API_BASE_URL}/collections/${id}`, {
        method: 'PATCH',
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(collectionData),
      });

      if (!response.ok) {
        const errorBody = await response.text();
        console.error("Update collection error response:", errorBody);
        throw new Error(`Failed to update collection: ${response.status} ${response.statusText} - ${errorBody}`);
      }

      const collection = await response.json();
      const transformedCollection = this.transformCollection(collection);
      
      toast.success('Collection updated successfully!');
      return transformedCollection;
    } catch (error) {
      console.error(`Error updating collection ${id}:`, error);
      return handleApiError(error);
    }
  },

  /**
   * Delete a collection permanently
   */
  async deleteCollection(id: string): Promise<void> {
    try {
      console.log(`🗑️ Deleting collection ${id}...`);
      
      const response = await fetch(`${API_BASE_URL}/collections/${id}`, {
        method: 'DELETE',
        headers: {
          'X-API-Token': API_TOKEN,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete collection: ${response.status} ${response.statusText}`);
      }
      
      toast.success('Collection deleted successfully!');
    } catch (error) {
      console.error(`Error deleting collection ${id}:`, error);
      return handleApiError(error);
    }
  },

  /**
   * Toggle collection visibility (public/private)
   */
  async toggleCollectionVisibility(id: string): Promise<Collection> {
    try {
      console.log(`🔄 Toggling visibility for collection ${id}...`);
      
      const response = await fetch(`${API_BASE_URL}/collections/${id}/visibility`, {
        method: 'PATCH',
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to toggle collection visibility: ${response.status} ${response.statusText}`);
      }

      const collection = await response.json();
      const transformedCollection = this.transformCollection(collection);
      
      const newVisibility = transformedCollection.visibility === 'public' ? 'public' : 'private';
      toast.success(`Collection is now ${newVisibility}`);
      
      return transformedCollection;
    } catch (error) {
      console.error(`Error toggling visibility for collection ${id}:`, error);
      return handleApiError(error);
    }
  },

  /**
   * Get documents in a collection
   */
  async getCollectionDocuments(id: string): Promise<any[]> {
    try {
      console.log(`📄 Fetching documents for collection ${id}...`);
      
      const response = await fetch(`${API_BASE_URL}/collections/${id}/documents`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch collection documents: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.documents || [];
    } catch (error) {
      console.error(`Error fetching documents for collection ${id}:`, error);
      throw error;
    }
  },

  /**
   * Add documents to a collection
   */
  async addDocumentsToCollection(id: string, documentIds: string[]): Promise<void> {
    try {
      console.log(`📄 Adding documents to collection ${id}...`, documentIds);
      
      const response = await fetch(`${API_BASE_URL}/collections/${id}/documents`, {
        method: 'POST',
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ document_ids: documentIds }),
      });

      if (!response.ok) {
        throw new Error(`Failed to add documents to collection: ${response.status} ${response.statusText}`);
      }
      
      toast.success(`Added ${documentIds.length} document(s) to collection`);
    } catch (error) {
      console.error(`Error adding documents to collection ${id}:`, error);
      return handleApiError(error);
    }
  },

  /**
   * Remove a document from a collection
   */
  async removeDocumentFromCollection(collectionId: string, documentId: string): Promise<void> {
    try {
      console.log(`📄 Removing document ${documentId} from collection ${collectionId}...`);
      
      const response = await fetch(`${API_BASE_URL}/collections/${collectionId}/documents/${documentId}`, {
        method: 'DELETE',
        headers: {
          'X-API-Token': API_TOKEN,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to remove document from collection: ${response.status} ${response.statusText}`);
      }
      
      toast.success('Document removed from collection');
    } catch (error) {
      console.error(`Error removing document from collection ${collectionId}:`, error);
      return handleApiError(error);
    }
  },

  /**
   * Transform raw API collection to our Collection interface
   */
  transformCollection(collection: any): Collection {
    return {
      id: collection.id?.toString() || collection.id,
      name: collection.name || '',
      description: collection.description || '',
      created_by: collection.created_by || 1,
      created_at: normalizeTimestamp(collection.created_at),
      updated_at: normalizeTimestamp(collection.updated_at || collection.created_at),
      status: collection.status || 'active',
      visibility: collection.visibility || 'public',
      document_count: collection.document_count || 0,
      documents: collection.documents || [],
    };
  },

  /**
   * Get collection count with optional filtering
   */
  async getCollectionCount(filters?: CollectionFilter): Promise<number> {
    try {
      const params = new URLSearchParams();
      
      if (filters) {
        if (filters.status) {
          filters.status.forEach(status => params.append('status', status));
        }
        if (filters.visibility) {
          filters.visibility.forEach(visibility => params.append('visibility', visibility));
        }
        if (filters.search_term) {
          params.append('search_term', filters.search_term);
        }
      }

      const response = await fetch(`${API_BASE_URL}/collections/count?${params.toString()}`, {
        headers: {
          'X-API-Token': API_TOKEN,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch collection count: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      return data.total || 0;
    } catch (error) {
      console.error('Error getting collection count:', error);
      throw error;
    }
  },

  /**
   * Check if current user has permission for collection operations
   */
  checkCollectionPermissions(action: 'create' | 'edit' | 'delete' | 'view'): boolean {
    // For now, assume user role is stored in localStorage
    // This should be replaced with proper auth context when available
    const userRole = localStorage.getItem('user_role') || 'subscriber';
    
    switch (action) {
      case 'view':
        // All roles can view collections
        return ['admin', 'editor', 'contributor', 'subscriber'].includes(userRole);
      case 'create':
      case 'edit':
        // Admin, editor, and contributor can create/edit
        return ['admin', 'editor', 'contributor'].includes(userRole);
      case 'delete':
        // Only admin and editor can delete
        return ['admin', 'editor'].includes(userRole);
      default:
        return false;
    }
  }
};

export default CollectionsService;
